import { useCallback, useRef, useState } from "react";
import {
  MAX_BUFFERED_AMOUNT_THRESHOLD,
  MAX_CONCURRENT_TRANSFERS_PER_PROJECT,
  MAX_UPLOAD_BATCH_FILES,
  MAX_UPLOAD_BATCH_TOTAL_BYTES,
  MAX_UPLOAD_CHUNK_BYTES,
  MAX_UPLOAD_FILE_BYTES,
} from "@racler/shared";
import { encodeTransferChunk } from "@racler/protocol";
import { RelayError, type RelayClient } from "./relayClient.ts";

export type TransferStatus = "queued" | "uploading" | "collision" | "completed" | "error" | "cancelled";

export interface TransferItem {
  id: string;
  file: File;
  targetDir: string;
  fileName: string;
  size: number;
  bytesUploaded: number;
  status: TransferStatus;
  error?: string;
  collisionPolicy?: "overwrite" | "rename";
  completedPath?: string;
}

export interface UseFileTransferOptions {
  relay: RelayClient;
  machineId: string;
  projectId: string;
  onComplete?: (targetDirs: string[]) => void;
  onError?: (message: string) => void;
}

export function validateUploadBatch(files: { size: number }[]): { ok: true } | { ok: false; error: string } {
  if (files.length > MAX_UPLOAD_BATCH_FILES) {
    return { ok: false, error: `Cannot upload more than ${MAX_UPLOAD_BATCH_FILES} files in a single batch.` };
  }
  const totalBatchBytes = files.reduce((sum, f) => sum + f.size, 0);
  if (totalBatchBytes > MAX_UPLOAD_BATCH_TOTAL_BYTES) {
    return {
      ok: false,
      error: `Total upload size exceeds ${MAX_UPLOAD_BATCH_TOTAL_BYTES / (1024 * 1024)} MB limit.`,
    };
  }
  return { ok: true };
}

export interface StreamTransferDeps {
  machineId: string;
  projectId: string;
  targetDir: string;
  file: File;
  collisionPolicy?: "overwrite" | "rename";
  relay: RelayClient;
  onProgress?: (bytesUploaded: number) => void;
  signal?: AbortSignal;
}

export type StreamTransferResult =
  | { status: "completed"; path: string }
  | { status: "collision" }
  | { status: "cancelled" };

export async function streamFileUpload(
  transferId: string,
  deps: StreamTransferDeps,
): Promise<StreamTransferResult> {
  const { machineId, projectId, targetDir, file, collisionPolicy, relay, onProgress, signal } = deps;

  // 1. Start transfer
  try {
    await relay.machine(machineId, "transfer/transfer.start", {
      transferId,
      projectId,
      destinationDir: targetDir,
      fileName: file.name,
      size: file.size,
      mimeType: file.type || undefined,
      collisionPolicy,
    });
  } catch (err) {
    if (err instanceof RelayError && err.code === "ALREADY_EXISTS") {
      return { status: "collision" };
    }
    throw err;
  }

  // 2. Stream binary chunks
  let offset = 0;
  while (offset < file.size) {
    if (signal?.aborted) break;

    await relay.waitForDrain(MAX_BUFFERED_AMOUNT_THRESHOLD);
    if (signal?.aborted) break;

    const end = Math.min(offset + MAX_UPLOAD_CHUNK_BYTES, file.size);
    const slice = file.slice(offset, end);
    const buffer = await slice.arrayBuffer();
    const chunkData = new Uint8Array(buffer);
    const framed = encodeTransferChunk(transferId, offset, chunkData);

    relay.sendBinary(framed);
    offset += chunkData.length;
    onProgress?.(offset);
  }

  // 3. Complete or Cancel
  if (signal?.aborted) {
    await relay
      .machine(machineId, "transfer/transfer.cancel", {
        transferId,
        reason: "cancelled by user",
      })
      .catch(() => undefined);
    return { status: "cancelled" };
  }

  try {
    const res = await relay.machine(machineId, "transfer/transfer.complete", {
      transferId,
      expectedSize: file.size,
    });
    return { status: "completed", path: res.path };
  } catch (err) {
    if (signal?.aborted) {
      await relay
        .machine(machineId, "transfer/transfer.cancel", {
          transferId,
          reason: "cancelled by user",
        })
        .catch(() => undefined);
      return { status: "cancelled" };
    }
    throw err;
  }
}

export function useFileTransfer({
  relay,
  machineId,
  projectId,
  onComplete,
  onError,
}: UseFileTransferOptions) {
  const [items, setItems] = useState<TransferItem[]>([]);
  const [collisionItem, setCollisionItem] = useState<TransferItem | null>(null);

  const abortControllers = useRef(new Map<string, AbortController>());
  const queueRef = useRef<TransferItem[]>([]);
  const activeCountRef = useRef(0);
  const completedDirsRef = useRef(new Set<string>());

  const updateItem = useCallback((id: string, patch: Partial<TransferItem>) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...patch } : it)),
    );
  }, []);

  const processNext = useCallback(async () => {
    if (activeCountRef.current >= MAX_CONCURRENT_TRANSFERS_PER_PROJECT) return;
    const nextItem = queueRef.current.shift();
    if (!nextItem) {
      if (activeCountRef.current === 0 && completedDirsRef.current.size > 0) {
        const dirs = Array.from(completedDirsRef.current);
        completedDirsRef.current.clear();
        onComplete?.(dirs);
      }
      return;
    }

    activeCountRef.current++;
    const controller = new AbortController();
    abortControllers.current.set(nextItem.id, controller);

    updateItem(nextItem.id, { status: "uploading" });

    try {
      const result = await streamFileUpload(nextItem.id, {
        machineId,
        projectId,
        targetDir: nextItem.targetDir,
        file: nextItem.file,
        collisionPolicy: nextItem.collisionPolicy,
        relay,
        signal: controller.signal,
        onProgress: (offset) => updateItem(nextItem.id, { bytesUploaded: offset }),
      });

      if (result.status === "collision") {
        updateItem(nextItem.id, { status: "collision" });
        setCollisionItem(nextItem);
        return;
      } else if (result.status === "cancelled") {
        updateItem(nextItem.id, { status: "cancelled" });
      } else {
        updateItem(nextItem.id, {
          status: "completed",
          bytesUploaded: nextItem.size,
          completedPath: result.path,
        });
        completedDirsRef.current.add(nextItem.targetDir);
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        await relay
          .machine(machineId, "transfer/transfer.cancel", {
            transferId: nextItem.id,
            reason: (err as Error).message,
          })
          .catch(() => undefined);
      }
      updateItem(nextItem.id, {
        status: "error",
        error: (err as Error).message || "Upload failed",
      });
    } finally {
      abortControllers.current.delete(nextItem.id);
      activeCountRef.current--;
      void processNext();
    }
  }, [machineId, onComplete, projectId, relay, updateItem]);

  const uploadFiles = useCallback(
    (targetDir: string, files: File[]) => {
      if (files.length === 0) return;

      const batchCheck = validateUploadBatch(files);
      if (!batchCheck.ok) {
        onError?.(batchCheck.error);
        return;
      }

      const newItems: TransferItem[] = [];
      const validItems: TransferItem[] = [];

      for (const file of files) {
        const id = crypto.randomUUID();
        if (file.size > MAX_UPLOAD_FILE_BYTES) {
          newItems.push({
            id,
            file,
            targetDir,
            fileName: file.name,
            size: file.size,
            bytesUploaded: 0,
            status: "error",
            error: `File exceeds ${MAX_UPLOAD_FILE_BYTES / (1024 * 1024)} MB limit`,
          });
        } else {
          const item: TransferItem = {
            id,
            file,
            targetDir,
            fileName: file.name,
            size: file.size,
            bytesUploaded: 0,
            status: "queued",
          };
          newItems.push(item);
          validItems.push(item);
        }
      }

      setItems((prev) => [...prev, ...newItems]);
      queueRef.current.push(...validItems);

      const slots = MAX_CONCURRENT_TRANSFERS_PER_PROJECT - activeCountRef.current;
      for (let i = 0; i < slots; i++) {
        void processNext();
      }
    },
    [onError, processNext],
  );

  const cancelTransfer = useCallback(
    (id: string) => {
      const queuedIdx = queueRef.current.findIndex((it) => it.id === id);
      if (queuedIdx >= 0) {
        queueRef.current.splice(queuedIdx, 1);
        updateItem(id, { status: "cancelled" });
        return;
      }

      const controller = abortControllers.current.get(id);
      if (controller) {
        controller.abort();
      }
    },
    [updateItem],
  );

  const cancelAll = useCallback(() => {
    queueRef.current = [];
    for (const controller of abortControllers.current.values()) {
      controller.abort();
    }
    setItems((prev) =>
      prev.map((it) =>
        it.status === "queued" || it.status === "uploading"
          ? { ...it, status: "cancelled" }
          : it,
      ),
    );
    setCollisionItem(null);
  }, []);

  const resolveCollision = useCallback(
    (item: TransferItem, policy: "overwrite" | "rename" | "cancel") => {
      setCollisionItem(null);
      if (policy === "cancel") {
        updateItem(item.id, { status: "cancelled" });
        activeCountRef.current--;
        void processNext();
        return;
      }

      const freshId = crypto.randomUUID();
      const retryItem: TransferItem = {
        ...item,
        id: freshId,
        bytesUploaded: 0,
        status: "queued",
        collisionPolicy: policy,
      };

      setItems((prev) => prev.map((it) => (it.id === item.id ? retryItem : it)));
      queueRef.current.unshift(retryItem);
      activeCountRef.current--;
      void processNext();
    },
    [processNext, updateItem],
  );

  const dismiss = useCallback(() => {
    setItems((prev) =>
      prev.filter((it) => it.status === "uploading" || it.status === "queued"),
    );
  }, []);

  const totalBytes = items.reduce((s, it) => s + it.size, 0);
  const uploadedBytes = items.reduce((s, it) => s + it.bytesUploaded, 0);
  const isUploading = items.some(
    (it) => it.status === "uploading" || it.status === "queued",
  );
  const percent = totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;

  return {
    items,
    collisionItem,
    isUploading,
    totalBytes,
    uploadedBytes,
    percent,
    uploadFiles,
    cancelTransfer,
    cancelAll,
    resolveCollision,
    dismiss,
  };
}
